/**
 * Routine versions.
 *
 * The phantom is the physical object and never changes; the routine is the
 * AQS process that tests it and does change. A routine *version* is an
 * alternative evaluation + display for the same routine: same analysis
 * (AQS-testing-scripts never sees the version), same baseline, same trends,
 * same schedules, same scanner association, same questions. A test records
 * the version that evaluated it in `phantom_test.version` (null = the
 * routine's default evaluation, otherwise a version id below) and only the
 * evaluation / display logic reads it.
 *
 * Which versions a routine has is decided by its `phantom` key -- the process
 * it runs -- so every routine running `mg` offers the same versions. Versions
 * are hidden from regular users: the study page shows the selector only while
 * Shift is held over the routine's button.
 *
 * Adding a version: add an entry here and an evaluation function under the
 * same id in `routineVersionEvaluations` (server evaluateTest.module.ts). A
 * jest test keeps the two in sync.
 *
 * Making a version the routine's default: point the routine's entry in
 * `evaluations` (same server file) at its function, mark it `isDefault` here
 * and give the previous default an id of its own, so a test that stored
 * either id keeps resolving.
 */
import { Phantom } from "./aqsTypes";

export interface RoutineVersion {
  /** stored in phantom_test.version; unique within the routine's phantom key */
  id: string;
  label: string;
  /**
   * This version is what the routine's default evaluation does now. Its id
   * still resolves, for tests that stored it before it became the default,
   * but it is not offered or labelled as an alternative any more.
   */
  isDefault?: boolean;
}

export const ROUTINE_VERSIONS: { [phantom in Phantom]?: RoutineVersion[] } = {
  [Phantom.mg]: [
    // the flat series list: MG's default until 3.22.0
    { id: "series_list", label: "Series list" },
    // MG's default since 3.22.0
    { id: "by_phantom", label: "Grouped by phantom", isDefault: true },
  ],
};

/** every id a test of this phantom key may have stored */
function declaredVersions(phantom: Phantom | string): RoutineVersion[] {
  return ROUTINE_VERSIONS[phantom as Phantom] || [];
}

/** the versions a routine with this phantom key offers besides its default (`[]` when none) */
export function routineVersions(phantom: Phantom | string): RoutineVersion[] {
  return declaredVersions(phantom).filter((v) => !v.isDefault);
}

/** null / undefined is the default evaluation and always valid; so is every declared id */
export function isKnownRoutineVersion(
  phantom: Phantom | string,
  version: string | null | undefined
): boolean {
  if (version === null || version === undefined) {
    return true;
  }
  return declaredVersions(phantom).some((v) => v.id === version);
}

/** the label of the version that evaluated a test, when that is not the routine's default */
export function routineVersionLabel(
  phantom: Phantom | string,
  version: string | null | undefined
): string | undefined {
  if (version === null || version === undefined) {
    return undefined;
  }
  const found = declaredVersions(phantom).filter((v) => v.id === version)[0];
  if (!found) {
    return version;
  }
  return found.isDefault ? undefined : found.label;
}
